import { useFormWithCleanup as useForm } from './useFormWithCleanup';
import { z } from 'zod';

import type { ChangePasswordRequest } from '@/types/auth.types';

const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, 'validation.required'),
    newPassword: z
      .string()
      .min(6, 'validation.passwordMinLength')
      .max(128, 'validation.passwordMaxLength'),
    confirmPassword: z.string().min(1, 'validation.required'),
  })
  // The empty-confirmation case is left to its own min(1) rule so an untouched
  // field reports "required" rather than "passwords do not match".
  .refine(
    (values) => values.confirmPassword === '' || values.newPassword === values.confirmPassword,
    {
      path: ['confirmPassword'],
      message: 'validation.passwordMismatch',
    },
  );

const emptyDefaults = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
};

export function useChangePasswordForm() {
  const form = useForm({
    defaultValues: { ...emptyDefaults },
    validators: {
      onBlur: changePasswordSchema,
    },
  });

  const isValid = form.useStore((state) => changePasswordSchema.safeParse(state.values).success);

  function reset(): void {
    form.update({ defaultValues: { ...emptyDefaults } });
    form.reset();
  }

  // Passwords are deliberately NOT trimmed: leading/trailing spaces are
  // legal password characters, and login does not trim either.
  function toRequest(): ChangePasswordRequest {
    return {
      currentPassword: form.state.values.currentPassword,
      newPassword: form.state.values.newPassword,
    };
  }

  return {
    form,
    reset,
    toRequest,
    isValid,
  };
}
